import { ConnectionState, type Room } from 'livekit-client'

/**
 * "My camera is on, but waiting for its background" — told to the other
 * participants over a LiveKit data message (SC-158, F8).
 *
 * To them a camera held for its effect is otherwise indistinguishable from one
 * turned off: both are a muted or absent publication. This is a client-to-client
 * contract; the server only relays it.
 *
 * - **Topic** {@link CAMERA_STATE_TOPIC}, reliable.
 * - **Payload** UTF-8 JSON `{ "v": 1, "waiting": boolean }`. Receivers ignore
 *   other topics, other versions and anything malformed, so a later version can
 *   change the shape without confusing this one.
 * - **Identity** is the sender LiveKit reports, never anything in the payload.
 * - Absent means not waiting. So nothing is sent until the state first becomes
 *   true — a user who never picks an effect sends nothing at all — and a client
 *   that does not know the topic (an older bundle) keeps showing "camera off".
 */
export const CAMERA_STATE_TOPIC = 'sevv.camera-state'

const CAMERA_STATE_VERSION = 1

/** Far above `{"v":1,"waiting":false}`; anything bigger is not ours. */
const MAX_PAYLOAD_BYTES = 256

export function encodeCameraWaiting(waiting: boolean): Uint8Array {
  return new TextEncoder().encode(JSON.stringify({ v: CAMERA_STATE_VERSION, waiting }))
}

/** The `waiting` flag, or null for anything this version does not understand. */
export function decodeCameraWaiting(payload: Uint8Array): boolean | null {
  if (!payload || payload.byteLength > MAX_PAYLOAD_BYTES) return null
  try {
    const message = JSON.parse(new TextDecoder().decode(payload)) as {
      v?: unknown
      waiting?: unknown
    } | null
    if (!message || message.v !== CAMERA_STATE_VERSION) return null
    return typeof message.waiting === 'boolean' ? message.waiting : null
  } catch {
    return null
  }
}

export interface CameraWaitingSender {
  /** The local state, whenever it may have changed. Sends only a change. */
  update: (room: Room | null, waiting: boolean) => void
  /** Someone joined: they have not heard it. */
  participantJoined: (room: Room, identity: string) => void
  /** Connected or reconnected: anyone may have missed it. */
  connected: (room: Room) => void
}

/**
 * The sending half, one per connector.
 *
 * Remembers what the Room was last told, so a state that does not change is
 * never sent twice, and a change made while disconnected is sent on reconnect.
 */
export function createCameraWaitingSender(): CameraWaitingSender {
  let waiting = false
  let toldRoom: Room | null = null
  /** What `toldRoom` was last told; receivers start from "not waiting". */
  let told = false

  const send = (room: Room, value: boolean, destinationIdentities?: string[]) => {
    room.localParticipant
      .publishData(encodeCameraWaiting(value), {
        reliable: true,
        topic: CAMERA_STATE_TOPIC,
        ...(destinationIdentities ? { destinationIdentities } : {}),
      })
      .catch((error: unknown) => {
        console.warn('[LiveKit] SC-158: could not send the camera state:', error)
      })
  }

  const flush = (room: Room | null) => {
    if (!room) return
    if (room !== toldRoom) {
      toldRoom = room
      told = false
    }
    if (room.state !== ConnectionState.Connected) return
    if (told === waiting) return
    told = waiting
    send(room, waiting)
  }

  return {
    update: (room, next) => {
      waiting = next
      flush(room)
    },
    participantJoined: (room, identity) => {
      if (room !== toldRoom || !waiting || room.state !== ConnectionState.Connected) return
      send(room, true, [identity])
    },
    connected: (room) => {
      // A full reconnect may have dropped us on every other client, which
      // forgets a participant that leaves; a resume did not. Saying it again
      // is harmless either way.
      if (room === toldRoom && waiting) told = false
      flush(room)
    },
  }
}
